import kitchenReadinessClinic from "../../assets/img/promos/kitchen-readiness-clinic.png";

/** How long a promo stays on screen once shown. `null` keeps it open until dismissed. */
export const DEFAULT_PROMO_DISPLAY_DURATION_MS: number | null = 12000;

/** Pause after the loading screen so the hero paints before the promo opens. */
export const DEFAULT_PROMO_SHOW_DELAY_MS = 600;

/** Routes that never show promotional content, even if a campaign allows them. Prefix match. */
export const DEFAULT_PROMO_IGNORED_ROUTES = [
  // "/pawdit",
  "/compliance",
  "/terms",
  "/privacy",
  "/disclaimer",
  "/eula",
  "/faq",
];

export type PromoMode = "media" | "embed" | "media-and-embed";

export type PromoCampaign = {
  id: string;
  enabled: boolean;
  /** ISO 8601 with offset, e.g. `2026-10-16T00:00:00-04:00`. */
  startsAt: string;
  endsAt: string;
  displayDurationMs?: number | null;
  showDelayMs?: number;
  /** Exact `/` matches only the home page; any other entry also matches its sub-paths. */
  showOnRoutes: string[];
  ignoredRoutes?: string[];
  mode: PromoMode;
  /** Accessible name for the dialog; not rendered as visible copy. */
  title: string;
  eyebrow?: string;
  media?: { src: string; alt: string };
  embedUrl?: string;
  embedTitle?: string;
  cta?: { label: string; href: string };
};

/** First enabled campaign whose window contains now wins. */
export const promoCampaigns: PromoCampaign[] = [
  {
    id: "kitchen-readiness-clinic-2026-11",
    enabled: true,
    startsAt: "2026-10-16T00:00:00-04:00",
    endsAt: "2026-11-23T16:30:00-05:00",
    displayDurationMs: 12000,
    showDelayMs: DEFAULT_PROMO_SHOW_DELAY_MS,
    showOnRoutes: ["/", "/food-entrepreneurs", "/resource-providers", "/pawdit"],
    mode: "media",
    title: "From Kitchen Readiness to Profitable Growth — free operating clinic",
    eyebrow: "Free · In person · Limited seats",
    media: {
      src: kitchenReadinessClinic,
      alt: "From Kitchen Readiness to Profitable Growth: a free operating clinic for food entrepreneurs and restaurant operators with HomeCook and AiriWheels, Monday November 23, 2026, 3:00 to 4:30 PM ET at MIT Innovation HQ, 292 Main St, Cambridge, MA. Includes a QR code to reserve at luma.com/zcaxtqsh.",
    },
    cta: { label: "Reserve your seat", href: "https://luma.com/zcaxtqsh" },
  },
];

/**
 * Collapses email-link path variants to the route they belong to.
 * Drops a trailing slash, decodes escapes, and cuts a query or hash glued onto the path.
 */
export const normalizePromoPath = (pathname: string): string => {
  let path = pathname.trim();
  try {
    path = decodeURIComponent(path);
  } catch {
    // Leave a malformed escape sequence as written.
  }
  const cut = path.search(/[?&#]/);
  if (cut >= 0) path = path.slice(0, cut);
  if (path.length > 1) path = path.replace(/\/+$/, "");
  return path === "" ? "/" : path;
};

/**
 * Returns true when `pathname` equals `route` or sits beneath it.
 * `/` only matches the home page so it does not act as a catch-all.
 */
export const matchesRoute = (pathname: string, route: string): boolean => {
  const path = normalizePromoPath(pathname);
  const normalizedRoute = normalizePromoPath(route);
  if (normalizedRoute === "/") return path === "/";
  return path === normalizedRoute || path.startsWith(`${normalizedRoute}/`);
};

/** Returns true when the campaign window contains `now` (inclusive start, exclusive end). */
export const isWithinCampaignWindow = (
  campaign: PromoCampaign,
  now: number,
): boolean => {
  const start = Date.parse(campaign.startsAt);
  const end = Date.parse(campaign.endsAt);
  if (Number.isNaN(start) || Number.isNaN(end)) return false;
  return now >= start && now < end;
};

/** Returns true when the route is allowed by the campaign and not on its ignore list. */
export const isRouteEligible = (
  campaign: PromoCampaign,
  pathname: string,
): boolean => {
  const ignored = campaign.ignoredRoutes ?? DEFAULT_PROMO_IGNORED_ROUTES;
  if (ignored.some((route) => matchesRoute(pathname, route))) return false;
  return campaign.showOnRoutes.some((route) => matchesRoute(pathname, route));
};

/** Picks the campaign to show for a path and time, or `null` when nothing applies. */
export const getActivePromoCampaign = (
  pathname: string,
  now: number,
  campaigns: PromoCampaign[] = promoCampaigns,
): PromoCampaign | null =>
  campaigns.find(
    (campaign) =>
      campaign.enabled &&
      isWithinCampaignWindow(campaign, now) &&
      isRouteEligible(campaign, pathname),
  ) ?? null;

/** Resolves the effective on-screen duration, falling back to the shared default. */
export const getDisplayDurationMs = (campaign: PromoCampaign): number | null =>
  campaign.displayDurationMs === undefined
    ? DEFAULT_PROMO_DISPLAY_DURATION_MS
    : campaign.displayDurationMs;
